import React, { useState, useEffect } from 'react';
import Fetch from '../services/fetchService';
import AuthData from '../common/AuthData';
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import Error from '../common/components/ErrorMessage';
import Success from '../common/components/SuccessMessage';
import YesNoPopup from '../common/components/YesNoPopup';
import Post from '../services/postService';
import { FaTrash, FaPlus, FaCopy, FaExclamationCircle } from 'react-icons/fa'; // Import icons
import '../styles/Settings.css';
import { SETTINGS_API } from '../common/ResourcePaths'; // Import the constants

function OpeningHours() {
    const [loader, setLoader] = useState(true);
    const [modified, setModified] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [successTimer, setSuccessTimer] = useState(10);
    const [disabled, setDisabled] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [openingHours, setOpeningHours] = useState([
        { day: 'Monday', open: '', close: '' },
        { day: 'Tuesday', open: '', close: '' },
        { day: 'Wednesday', open: '', close: '' },
        { day: 'Thursday', open: '', close: '' },
        { day: 'Friday', open: '', close: '' },
        { day: 'Saturday', open: '', close: '' },
        { day: 'Sunday', open: '', close: '' }
    ]);

    const handleInputChange = (day, field, value) => {
        setModified(true);
        setOpeningHours(prevHours => 
            prevHours.map(hour => 
                hour.day === day 
                    ? { ...hour, [field]: value }
                    : hour
            )
        );
    };

    const handleRemoveDay = (day) => {
        setOpeningHours(prevHours => prevHours.filter(hour => hour.day !== day));
        setModified(true);
    };

    const handleAddDay = () => {
        const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const existingDays = openingHours.map(hour => hour.day);
        const nextDay = allDays.find((day) => !existingDays.includes(day));

        if (nextDay) {
            setOpeningHours(prevHours => [...prevHours, { day: nextDay, open: '', close: '' }]);
            setModified(true);
        } else {
            console.warn('All days have already been added.');
        }
    };

    const handleCopyPreviousDay = () => {
        const days = Object.keys(openingHours);
        if (days.length > 0) {
            const lastDay = days[days.length - 1];
            const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const nextDay = allDays.find((day) => !days.includes(day));

            if (nextDay) {
                setOpeningHours({
                    ...openingHours,
                    [nextDay]: { ...openingHours[lastDay] },
                });
                setModified(true);
            }
        }
    };

    const reset = () => {
        setOpeningHours([
            { day: 'Monday', open: '', close: '' },
            { day: 'Tuesday', open: '', close: '' },
            { day: 'Wednesday', open: '', close: '' },
            { day: 'Thursday', open: '', close: '' },
            { day: 'Friday', open: '', close: '' },
            { day: 'Saturday', open: '', close: '' },
            { day: 'Sunday', open: '', close: '' }
        ]);
        setDisabled(false);
        setModified(true);
    };

    const handleOpeningHoursDisabledChange = (e) => {
        setDisabled(e.target.checked);
        setModified(true);
    };

    const handleYes = () => {
        setShowPopup(false);
        reset();
    };

    const handleNo = () => {
        setShowPopup(false);
    };

    // Method to format data for the view
    const formatDataForView = (response) => {
        // If response is already an array, return it
        if (Array.isArray(response)) {
            return response.map(item => ({
                day: item.day,
                open: item.open,
                close: item.close
            }));
        }
        // If response is an object, convert to array
        return response.reduce((acc, item) => {
            acc.push({ day: item.day, open: item.open, close: item.close });
            return acc;
        }, []);
    };

    useEffect(() => {
        const getOpeningHours = async () => {
            try {
                setLoader(true);
                const authData = AuthData.getAuthData();
                const url = `${authData.appFunctionUrl}${SETTINGS_API}/openingHours?code=${authData.azureFunctionKey}`; // Use the constant here
                const response = await Fetch(url, authData.clientId);
                const formattedData = formatDataForView(response.hours || response);
                setOpeningHours(formattedData);
                // Match the backend JSON property name
                setDisabled(response.disabled || false);
                
            } catch (error) {
                console.error("Error fetching data:", error);
                setError("An error occurred while saving data.");
            } finally {
                setLoader(false);
                setModified(false);
            }
        };

        getOpeningHours();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); 
        setLoader(true);

        const authData = AuthData.getAuthData();
        const url = `${authData.appFunctionUrl}${SETTINGS_API}/hours?code=${authData.azureFunctionKey}`; // Use the constant here

        const data = {
            hours: openingHours.map((hour) => ({
                day: hour.day,
                open: hour.open,
                close: hour.close,
            })),
            disabled: disabled
        };

        try {
            const response = await Post(url,authData.clientId, data);
            const formattedData = formatDataForView(response.hours || response);
            setOpeningHours(formattedData);
            setDisabled(response.disabled || false);
            // Start countdown timer
            setSuccess(true); // Show success message
            setSuccessTimer(10); // Reset timer to 10 seconds

            const timerInterval = setInterval(() => {
                    setSuccessTimer((prev) => {
                        if (prev <= 1) {
                            clearInterval(timerInterval);
                            setSuccess(false);
                            return 0;
                        }
                        return prev - 1;
                });
            }, 1000);
        } catch (error) {
            console.error('Error saving data:', error);
            setError("An error occurred while saving data.");
        } finally {
            setLoader(false);
            setError('');
            setModified(false); // Reset modified state
        }
    };

    return (
        <div className="container" style={{ position: 'relative' }}>
            {loader && <RelativeSvgLoader />}
            <div className="form-wrapper" style={{ opacity: loader ? 0.3 : 1 }}>
                {error && <Error message={error} />}
                {success && <Success message={`Data saved successfully! (${successTimer}s)`} />}
                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '15px'}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 'bold'}}>
                        <input
                            type="checkbox"
                            checked={disabled}
                            onChange={handleOpeningHoursDisabledChange}
                        />
                        Disable
                        <div style={{position: 'relative', display: 'inline-block'}}>
                            <FaExclamationCircle
                                style={{
                                    color: '#28a745',
                                    fontSize: '14px',
                                    marginLeft: '4px',
                                    cursor: 'help'
                                }}
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            />
                            {showTooltip && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '20px',
                                    right: '0',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 1000,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                    When checked, hides opening hours from customers
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: '10px',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '5px solid transparent',
                                        borderRight: '5px solid transparent',
                                        borderTop: '5px solid #28a745'
                                    }}></div>
                                </div>
                            )}
                        </div>
                    </label>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        {openingHours.map((hour) => (
                            <React.Fragment key={hour.day}>
                                <label className="form-label">{hour.day}:</label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input
                                        type="time"
                                        className="input-field"
                                        value={hour.open}
                                        onChange={(e) => handleInputChange(hour.day, 'open', e.target.value)}
                                        readOnly={disabled}
                                        placeholder="Open"
                                    />
                                    <input
                                        type="time"
                                        className="input-field"
                                        value={hour.close}
                                        onChange={(e) => handleInputChange(hour.day, 'close', e.target.value)}
                                        readOnly={disabled}
                                        placeholder="Close"
                                    />
                                    <FaTrash
                                        style={{ cursor: 'pointer', color: 'red', fontSize: '16px' }}
                                        onClick={() => handleRemoveDay(hour.day)}
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                        {openingHours.length < 7 && (
                            <button
                                type="button"
                                className="submit-button"
                                onClick={handleCopyPreviousDay}
                                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                            >
                                <FaCopy />
                            </button>
                        )}
                        {Object.keys(openingHours).length < 7 && (
                            <button
                                type="button"
                                className="submit-button"
                                onClick={handleAddDay}
                                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                            >
                                <FaPlus />
                            </button>
                        )}
                    </div>
                    <div className='form-footer'>
                        <button type="submit" disabled={!modified} className="submit-button">Save</button>
                        <button type="button"
                            className='clear-data-button'
                            style={{ marginLeft: '10px' }} onClick={() => setShowPopup(true)}>Reset</button>
                    </div>
                </form>
            </div>
            {showPopup && (
                <YesNoPopup
                    handleYes={handleYes}
                    handleNo={handleNo} />
            )}
        </div>
    );
}

export default OpeningHours;